import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { getIsImperialSystem } from 'ha/pages/Search/selectors';

import { Grid, FormControl, Select, Button } from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import {
  RadiusValue,
  RadiusValueInMiles,
  RadiusFilterValue,
} from 'ha/types/SearchFilters';
import { MenuItem } from 'ha/components/Menu/MenuItem';

import { Props as SearchAreaProps, SearchAreaFilter } from './SearchAreaFilter';
import { Props as PlaceFilterProps, PlaceFilter } from './PlaceFilter';

interface Props {
  placeProps: PlaceFilterProps;
  searchAreaProps: SearchAreaProps;
  radiusProps: {
    value: RadiusFilterValue;
    onChange: (value: RadiusValue) => void;
  };
  hideReset?: boolean;
}

const useStyles = makeStyles()(theme => ({
  defaultValueSelect: {
    color: theme.palette.grey['400'],
  },
  defaultValueOption: {
    display: 'none',
  },
  resetButton: {
    marginLeft: -theme.spacing(1),
    marginTop: -theme.spacing(2),
    textTransform: 'none',
    color: theme.palette.secondary.dark,
    justifyContent: 'flex-start',
  },
}));

const LocationFilter: React.FC<Props> = ({
  placeProps,
  searchAreaProps,
  radiusProps: { value: radiusValue, onChange: onRadiusChange },
  hideReset,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const isImperialSystem = useSelector(getIsImperialSystem);

  const handleRadiusChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onRadiusChange(e.target.value as RadiusValue);
    },
    [onRadiusChange],
  );

  const handleRadiusClosed = React.useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const handleResetClick = React.useCallback(() => {
    onRadiusChange(RadiusValue.DEFAULT);
  }, [onRadiusChange]);

  const isDefaultRadius = radiusValue === RadiusValue.DEFAULT;

  const menuRadius = useMemo(() => {
    return !isImperialSystem ? RadiusValue : RadiusValueInMiles;
  }, [isImperialSystem]);

  const menuOptions = useMemo(() => {
    return Object.keys(menuRadius).map(key => menuRadius[key]);
  }, [menuRadius]);

  return (
    <Grid container>
      {!hideReset && !isDefaultRadius && (
        <Grid item>
          <Button
            variant="text"
            color="secondary"
            onClick={handleResetClick}
            className={classes.resetButton}
          >
            {T('search.filters.location.reset')}
          </Button>
        </Grid>
      )}
      <Grid item xs={12}>
        <PlaceFilter {...placeProps} />
      </Grid>
      <Grid item xs={12}>
        <FormControl
          margin="dense"
          fullWidth
          variant="outlined"
          hiddenLabel
          size="small"
        >
          <Select
            margin="dense"
            variant="outlined"
            value={radiusValue}
            onChange={handleRadiusChange}
            className={isDefaultRadius ? classes.defaultValueSelect : undefined}
            MenuProps={{
              TransitionProps: {
                onExited: handleRadiusClosed,
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            }}
          >
            {menuOptions.map((value: string) => {
              if (value === RadiusValue.DEFAULT) {
                return (
                  <MenuItem
                    key={value}
                    value={RadiusValue.DEFAULT}
                    disabled
                    className={classes.defaultValueOption}
                  >
                    {T('search.filters.location.option_choose_distance')}
                  </MenuItem>
                );
              }

              return (
                <MenuItem key={value} value={value}>
                  {T('distance.within')} {value}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <SearchAreaFilter {...searchAreaProps} />
      </Grid>
    </Grid>
  );
};

export { LocationFilter };
